import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, LogOut, Plus, Trash2, Edit, Image as ImageIcon, Save, Eye, EyeOff, Check, X, Shield, ShieldOff, BarChart3, Database, Users } from "lucide-react";
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { api, getToken, setTokens, clearToken, formatApiError, formatPrice, resolveImg, touchActivity, isIdleExpired } from "@/lib/api";
import { useApp } from "@/lib/AppContext";

const BG_HINTS = {
  hero: { label: "Главный экран (Hero)", hint: "Ночной европейский автобан, тёмное фото. Мин. 1920×1080px." },
  catalog: { label: "Каталог", hint: "Европейский авто у исторического здания. Высота 300-400px." },
  how_we_work: { label: "Как мы работаем", hint: "Тёмный фон с картой Европы или маршруты доставки." },
  services: { label: "Услуги", hint: "Логистический центр или таможенный терминал." },
  reviews: { label: "Отзывы", hint: "Светлый фон. Можно оставить пустым." },
  configurator: { label: "Конфигуратор", hint: "Вид из кабины авто на дорогу." },
  about: { label: "О компании", hint: "Российский город или команда." },
  contacts: { label: "Контакты", hint: "Тёмно-синий минималистичный фон." },
  blog: { label: "Новости", hint: "Европейская трасса с указателями." },
  thank_you: { label: "Страница «Спасибо»", hint: "Радостный момент покупки." },
};

export default function AdminPanel() {
  const navigate = useNavigate();
  const { reload } = useApp();
  const [authed, setAuthed] = useState(!!getToken());
  const [loading, setLoading] = useState(false);
  const [creds, setCreds] = useState({ username: "", password: "", totp_code: "" });
  const [need2fa, setNeed2fa] = useState(false);

  useEffect(() => {
    if (!authed) return;
    api.get("/auth/me").catch(() => { clearToken(); setAuthed(false); });
    // Activity tracking + idle auto-logout
    const onActivity = () => touchActivity();
    ["click", "keydown", "scroll", "mousemove"].forEach(ev => window.addEventListener(ev, onActivity, { passive: true }));
    const idleTimer = setInterval(() => {
      if (isIdleExpired()) {
        clearToken();
        setAuthed(false);
        toast.info("Вы были разлогинены из-за бездействия");
      }
    }, 60 * 1000);
    return () => {
      ["click", "keydown", "scroll", "mousemove"].forEach(ev => window.removeEventListener(ev, onActivity));
      clearInterval(idleTimer);
    };
  }, [authed]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/auth/login", creds);
      if (r.data.requires_2fa) {
        setNeed2fa(true);
        toast.info("Введите 6-значный код из Google Authenticator");
      } else if (r.data.access_token) {
        setTokens(r.data);
        setAuthed(true);
        toast.success("Добро пожаловать!");
      }
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Ошибка входа");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => { clearToken(); setAuthed(false); navigate("/"); };

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "var(--color-bg-dark)" }} data-testid="admin-login">
        <div className="w-full max-w-md bg-white rounded-[var(--radius-lg)] p-8" style={{ boxShadow: "var(--shadow-card)" }}>
          <Link to="/" className="font-accent text-xs uppercase tracking-widest text-[var(--color-text-secondary)] mb-2 inline-block hover:text-[var(--color-primary)]">← На сайт</Link>
          <h1 className="font-headings font-extrabold text-2xl mb-1">{need2fa ? "Двухфакторный код" : "Вход в админ-панель"}</h1>
          <p className="text-sm text-[var(--color-text-secondary)] mb-6">{need2fa ? "Введите код из Google Authenticator" : "Везём авто из Европы"}</p>
          <form onSubmit={login} className="space-y-3">
            {!need2fa && (
              <>
                <input className="field-input" placeholder="Логин" value={creds.username} onChange={e => setCreds(c => ({...c, username: e.target.value}))} required data-testid="admin-username" />
                <input className="field-input" type="password" placeholder="Пароль" value={creds.password} onChange={e => setCreds(c => ({...c, password: e.target.value}))} required data-testid="admin-password" />
              </>
            )}
            {need2fa && (
              <input className="field-input text-center text-2xl tracking-[0.4em] font-accent" placeholder="000000" maxLength={6} inputMode="numeric" value={creds.totp_code} onChange={e => setCreds(c => ({...c, totp_code: e.target.value.replace(/\D/g, "")}))} required autoFocus data-testid="admin-totp" />
            )}
            <button type="submit" disabled={loading} className="btn-primary-cta w-full" data-testid="admin-login-submit">
              {loading ? <Loader2 size={16} className="animate-spin" /> : (need2fa ? "Подтвердить" : "Войти")}
            </button>
            {need2fa && (
              <button type="button" onClick={() => { setNeed2fa(false); setCreds(c => ({...c, totp_code: ""})); }} className="w-full text-xs font-accent uppercase tracking-wider text-[var(--color-text-secondary)] hover:text-[var(--color-primary)]">
                Назад
              </button>
            )}
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--color-bg-primary)]" data-testid="admin-panel">
      <header className="sticky top-0 z-30 px-4 md:px-8 py-4 flex items-center justify-between border-b" style={{ background: "var(--color-secondary)", borderColor: "rgba(255,255,255,0.08)" }}>
        <Link to="/" className="font-headings font-extrabold text-white text-lg">Админ-панель</Link>
        <button onClick={logout} className="flex items-center gap-2 text-white/80 hover:text-white text-sm font-accent uppercase tracking-wider" data-testid="admin-logout">
          <LogOut size={16} /> Выйти
        </button>
      </header>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <Tabs defaultValue="analytics" className="w-full">
          <TabsList className="bg-white border flex-wrap h-auto" style={{ borderColor: "var(--color-border)" }}>
            <TabsTrigger value="analytics" data-testid="tab-analytics">Аналитика</TabsTrigger>
            <TabsTrigger value="cars" data-testid="tab-cars">Авто</TabsTrigger>
            <TabsTrigger value="applications" data-testid="tab-applications">Заявки</TabsTrigger>
            <TabsTrigger value="reviews" data-testid="tab-reviews">Отзывы</TabsTrigger>
            <TabsTrigger value="news" data-testid="tab-news">Новости</TabsTrigger>
            <TabsTrigger value="services" data-testid="tab-services">Услуги</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">Настройки</TabsTrigger>
            <TabsTrigger value="content" data-testid="tab-content">Контент</TabsTrigger>
          </TabsList>

          <TabsContent value="analytics" className="mt-6"><AnalyticsTab /></TabsContent>
          <TabsContent value="cars" className="mt-6"><CarsTab onChange={reload} /></TabsContent>
          <TabsContent value="applications" className="mt-6"><ApplicationsTab /></TabsContent>
          <TabsContent value="reviews" className="mt-6"><ReviewsTab /></TabsContent>
          <TabsContent value="news" className="mt-6"><NewsTab /></TabsContent>
          <TabsContent value="services" className="mt-6"><ServicesTab onChange={reload} /></TabsContent>
          <TabsContent value="settings" className="mt-6"><SettingsTab onChange={reload} /></TabsContent>
          <TabsContent value="content" className="mt-6"><ContentTab onChange={reload} /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

// ====== ANALYTICS TAB ======
const COLORS = ["#1C3A6B", "#B23A3A", "#D4A017", "#2D7A3A", "#E67E22", "#4A5568"];
function AnalyticsTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then(r => setData(r.data)); }, []);
  if (!data) return <div className="skeleton h-96" />;
  const totals = data.totals;
  return (
    <div className="space-y-6" data-testid="analytics-content">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Авто всего", v: totals.cars, c: "var(--color-secondary)" },
          { label: "В наличии", v: totals.cars_available, c: "var(--color-success)" },
          { label: "Заявок", v: totals.applications, c: "var(--color-primary)" },
          { label: "Отзывов на модерации", v: totals.reviews_pending, c: "var(--color-warning)" },
        ].map((s, i) => (
          <div key={i} className="bg-white rounded-[var(--radius-lg)] p-5 border" style={{ borderColor: "var(--color-border)" }}>
            <div className="font-accent text-xs uppercase tracking-widest text-[var(--color-text-secondary)]">{s.label}</div>
            <div className="font-headings font-extrabold text-3xl mt-2" style={{ color: s.c }}>{s.v}</div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-[var(--radius-lg)] p-5 border" style={{ borderColor: "var(--color-border)" }}>
        <h3 className="font-headings font-bold text-lg mb-4 flex items-center gap-2"><BarChart3 size={18} /> Заявки за 30 дней</h3>
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={data.applications_by_day}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eef1f6" />
            <XAxis dataKey="date" tick={{ fontSize: 10 }} interval={4} />
            <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
            <Tooltip />
            <Line type="monotone" dataKey="count" stroke="#B23A3A" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-[var(--radius-lg)] p-5 border" style={{ borderColor: "var(--color-border)" }}>
          <h3 className="font-headings font-bold text-lg mb-4">Заявки по типу</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={data.applications_by_type} dataKey="count" nameKey="type" outerRadius={80} label>
                {data.applications_by_type.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-[var(--radius-lg)] p-5 border" style={{ borderColor: "var(--color-border)" }}>
          <h3 className="font-headings font-bold text-lg mb-4">Топ просматриваемых авто</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.top_cars} layout="vertical" margin={{ left: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef1f6" />
              <XAxis type="number" tick={{ fontSize: 10 }} />
              <YAxis type="category" dataKey="brand" tick={{ fontSize: 10 }} />
              <Tooltip />
              <Bar dataKey="views_count" fill="#1C3A6B" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

// ====== NEWS TAB ======
function NewsTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/admin/news").then(r => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing.id) await api.put(`/admin/news/${editing.id}`, editing);
      else await api.post("/admin/news", editing);
      toast.success("Сохранено"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const remove = async (id) => { if (window.confirm("Удалить?")) { await api.delete(`/admin/news/${id}`); load(); } };
  const uploadCover = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const r = await api.post("/admin/upload?watermark=false", fd, { headers: { "Content-Type": "multipart/form-data" } }); setEditing(p => ({...p, cover_url: r.data.url})); }
    catch { toast.error("Ошибка загрузки"); }
  };

  return (
    <div data-testid="news-tab">
      <div className="flex justify-between mb-5">
        <h2 className="font-headings font-bold text-xl">Новости ({items.length})</h2>
        <button onClick={() => { setEditing({ title: "", excerpt: "", content: "", cover_url: "", is_published: true }); setOpen(true); }} className="btn-primary-cta" data-testid="admin-add-news"><Plus size={16} /> Добавить</button>
      </div>
      <div className="space-y-3">
        {items.map(n => (
          <div key={n.id} className="bg-white rounded-[var(--radius-lg)] p-5 border flex gap-4" style={{ borderColor: "var(--color-border)" }}>
            {n.cover_url && <img src={resolveImg(n.cover_url)} alt="" className="w-24 h-24 object-cover rounded" />}
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className={`text-xs px-2 py-0.5 rounded ${n.is_published ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>{n.is_published ? "Опубликовано" : "Черновик"}</span>
                <span className="text-xs text-[var(--color-text-secondary)]">{new Date(n.published_at).toLocaleDateString("ru-RU")}</span>
              </div>
              <div className="font-headings font-bold">{n.title}</div>
              <p className="text-sm text-[var(--color-text-secondary)] line-clamp-2">{n.excerpt}</p>
            </div>
            <div className="flex flex-col gap-1">
              <button onClick={() => { setEditing({...n}); setOpen(true); }} className="p-2 hover:bg-[var(--color-bg-primary)] rounded"><Edit size={16} /></button>
              <button onClick={() => remove(n.id)} className="p-2 hover:bg-red-50 text-[var(--color-primary)] rounded"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="bg-white p-8 text-center text-[var(--color-text-secondary)] rounded-[var(--radius-lg)]">Новостей пока нет</div>}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing?.id ? "Редактирование" : "Новая статья"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <input className="field-input" placeholder="Заголовок" value={editing.title} onChange={e => setEditing(p => ({...p, title: e.target.value}))} />
              <textarea className="field-input" rows={2} placeholder="Краткое описание (для карточки)" value={editing.excerpt} onChange={e => setEditing(p => ({...p, excerpt: e.target.value}))} />
              <textarea className="field-input" rows={10} placeholder="Текст статьи" value={editing.content} onChange={e => setEditing(p => ({...p, content: e.target.value}))} />
              <div className="flex items-center gap-3">
                {editing.cover_url && <img src={resolveImg(editing.cover_url)} alt="" className="w-24 h-16 object-cover rounded" />}
                <label className="btn-secondary-cta cursor-pointer text-xs">Загрузить обложку
                  <input type="file" accept="image/*" className="hidden" onChange={uploadCover} />
                </label>
                <input className="field-input flex-1" placeholder="или URL обложки" value={editing.cover_url} onChange={e => setEditing(p => ({...p, cover_url: e.target.value}))} />
              </div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.is_published} onChange={e => setEditing(p => ({...p, is_published: e.target.checked}))} /> Опубликовать</label>
              <button onClick={save} className="btn-primary-cta w-full" data-testid="admin-save-news"><Save size={16} /> Сохранить</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ====== CARS TAB ======
function CarsTab({ onChange }) {
  const [cars, setCars] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/admin/cars").then(r => setCars(r.data || []));
  useEffect(() => { load(); }, []);

  const empty = {
    brand: "", model: "", year: 2020, mileage: 0, price: 0,
    body_type: "sedan", fuel_type: "petrol", transmission: "automatic", color: "",
    description: "", photos: [], country_origin: "Германия", customs_cleared: false,
    pts_type: "Оригинал", owners_count: 1, euro_standard: "Euro 6", drive_type: "FWD",
    steering_wheel: "left", vin: "", euro_price: 0, delivery_days: 14, status: "available", is_featured: false,
  };

  const openNew = () => { setEditing({ ...empty }); setOpen(true); };
  const openEdit = (c) => { setEditing({ ...c }); setOpen(true); };

  const save = async () => {
    try {
      if (editing.id) {
        await api.put(`/admin/cars/${editing.id}`, editing);
      } else {
        await api.post("/admin/cars", editing);
      }
      toast.success("Сохранено");
      setOpen(false);
      load();
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Удалить авто?")) return;
    await api.delete(`/admin/cars/${id}`);
    load();
  };

  const [uploadingCount, setUploadingCount] = useState(0);

  const uploadPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadingCount(files.length);
    let done = 0;
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const r = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setEditing(p => ({ ...p, photos: [...(p.photos || []), r.data.url] }));
        done += 1;
        setUploadingCount(files.length - done);
      } catch (err) {
        toast.error(`Ошибка загрузки ${file.name}`);
        setUploadingCount(c => Math.max(0, c - 1));
      }
    }
    setUploadingCount(0);
    e.target.value = ""; // позволить повторный выбор тех же файлов
    if (done > 0) toast.success(`Загружено фото: ${done}`);
  };

  const movePhoto = (from, to) => {
    setEditing(p => {
      const arr = [...(p.photos || [])];
      if (to < 0 || to >= arr.length) return p;
      const [item] = arr.splice(from, 1);
      arr.splice(to, 0, item);
      return { ...p, photos: arr };
    });
  };

  return (
    <div>
      <div className="flex justify-between mb-5">
        <h2 className="font-headings font-bold text-xl">Каталог авто ({cars.length})</h2>
        <button onClick={openNew} className="btn-primary-cta" data-testid="admin-add-car"><Plus size={16} /> Добавить</button>
      </div>
      <div className="bg-white rounded-[var(--radius-lg)] overflow-hidden border" style={{ borderColor: "var(--color-border)" }}>
        <table className="w-full text-sm">
          <thead className="bg-[var(--color-bg-primary)]">
            <tr>
              <th className="text-left p-3 font-accent uppercase tracking-wider text-xs">Авто</th>
              <th className="text-left p-3 font-accent uppercase tracking-wider text-xs hidden md:table-cell">Цена</th>
              <th className="text-left p-3 font-accent uppercase tracking-wider text-xs hidden md:table-cell">Статус</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {cars.map(c => (
              <tr key={c.id} className="border-t" style={{ borderColor: "var(--color-border)" }}>
                <td className="p-3">
                  <div className="font-headings font-bold">{c.brand} {c.model}</div>
                  <div className="text-xs text-[var(--color-text-secondary)]">{c.year} · {c.country_origin}</div>
                </td>
                <td className="p-3 hidden md:table-cell">{formatPrice(c.price)}</td>
                <td className="p-3 hidden md:table-cell">
                  <span className={`status-badge status-${c.status}`}>{c.status === "available" ? "В наличии" : c.status === "order" ? "Под заказ" : "Продано"}</span>
                </td>
                <td className="p-3 text-right whitespace-nowrap">
                  <button onClick={() => openEdit(c)} className="p-2 hover:bg-[var(--color-bg-primary)] rounded" data-testid={`edit-car-${c.id}`}><Edit size={16} /></button>
                  <button onClick={() => remove(c.id)} className="p-2 hover:bg-red-50 text-[var(--color-primary)] rounded" data-testid={`delete-car-${c.id}`}><Trash2 size={16} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {cars.length === 0 && <div className="p-8 text-center text-[var(--color-text-secondary)]">Авто пока нет</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing?.id ? "Редактирование" : "Новый автомобиль"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <input className="field-input" placeholder="Марка" value={editing.brand} onChange={e => setEditing(p => ({...p, brand: e.target.value}))} />
                <input className="field-input" placeholder="Модель" value={editing.model} onChange={e => setEditing(p => ({...p, model: e.target.value}))} />
                <input className="field-input" type="number" placeholder="Год" value={editing.year} onChange={e => setEditing(p => ({...p, year: parseInt(e.target.value) || 0}))} />
                <input className="field-input" type="number" placeholder="Пробег" value={editing.mileage} onChange={e => setEditing(p => ({...p, mileage: parseInt(e.target.value) || 0}))} />
                <input className="field-input" type="number" placeholder="Цена ₽" value={editing.price} onChange={e => setEditing(p => ({...p, price: parseFloat(e.target.value) || 0}))} />
                <input className="field-input" type="number" placeholder="Цена в EUR" value={editing.euro_price} onChange={e => setEditing(p => ({...p, euro_price: parseFloat(e.target.value) || 0}))} />
                <select className="field-input" value={editing.body_type} onChange={e => setEditing(p => ({...p, body_type: e.target.value}))}>
                  <option value="sedan">Седан</option><option value="suv">Внедорожник</option><option value="hatchback">Хэтчбек</option>
                  <option value="wagon">Универсал</option><option value="coupe">Купе</option><option value="minivan">Минивэн</option>
                </select>
                <select className="field-input" value={editing.fuel_type} onChange={e => setEditing(p => ({...p, fuel_type: e.target.value}))}>
                  <option value="petrol">Бензин</option><option value="diesel">Дизель</option><option value="hybrid">Гибрид</option><option value="electric">Электро</option>
                </select>
                <select className="field-input" value={editing.transmission} onChange={e => setEditing(p => ({...p, transmission: e.target.value}))}>
                  <option value="automatic">Автомат</option><option value="manual">Механика</option><option value="robot">Робот</option><option value="variator">Вариатор</option>
                </select>
                <input className="field-input" placeholder="Цвет" value={editing.color} onChange={e => setEditing(p => ({...p, color: e.target.value}))} />
                <select className="field-input" value={editing.country_origin} onChange={e => setEditing(p => ({...p, country_origin: e.target.value}))}>
                  {["Германия","Франция","Чехия","Италия","Нидерланды","Польша","Австрия","Швеция"].map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <select className="field-input" value={editing.euro_standard} onChange={e => setEditing(p => ({...p, euro_standard: e.target.value}))}>
                  <option value="Euro 5">Euro 5</option><option value="Euro 6">Euro 6</option><option value="Euro 6d">Euro 6d</option>
                </select>
                <input className="field-input" type="number" placeholder="Доставка дней" value={editing.delivery_days} onChange={e => setEditing(p => ({...p, delivery_days: parseInt(e.target.value) || 0}))} />
                <input className="field-input" placeholder="VIN" value={editing.vin} onChange={e => setEditing(p => ({...p, vin: e.target.value}))} />
                <select className="field-input" value={editing.status} onChange={e => setEditing(p => ({...p, status: e.target.value}))}>
                  <option value="available">В наличии</option><option value="order">Под заказ</option><option value="sold">Продано</option>
                </select>
              </div>
              <textarea className="field-input" rows={3} placeholder="Описание" value={editing.description} onChange={e => setEditing(p => ({...p, description: e.target.value}))} />
              <div className="flex gap-4 text-sm">
                <label className="flex items-center gap-2"><input type="checkbox" checked={editing.customs_cleared} onChange={e => setEditing(p => ({...p, customs_cleared: e.target.checked}))} /> Растаможен</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={editing.is_featured} onChange={e => setEditing(p => ({...p, is_featured: e.target.checked}))} /> На главную</label>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="font-accent text-xs uppercase tracking-widest text-[var(--color-text-secondary)]">
                    Фото ({(editing.photos || []).length})
                    {uploadingCount > 0 && <span className="ml-2 text-[var(--color-primary)] normal-case">· загрузка осталось: {uploadingCount}</span>}
                  </div>
                  <div className="text-xs text-[var(--color-text-secondary)]">Первое фото — главное</div>
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-2">
                  {(editing.photos || []).map((p, i) => (
                    <div key={`${p}-${i}`} className="relative aspect-square rounded overflow-hidden border group" style={{ borderColor: i === 0 ? "var(--color-gold)" : "var(--color-border)", borderWidth: i === 0 ? 2 : 1 }} data-testid={`admin-car-photo-${i}`}>
                      <img src={resolveImg(p)} alt="" className="w-full h-full object-cover" />
                      {i === 0 && (
                        <span className="absolute top-1 left-1 bg-[var(--color-gold)] text-[var(--color-bg-dark)] text-[10px] font-bold px-1.5 py-0.5 rounded">ГЛАВНОЕ</span>
                      )}
                      <button type="button" onClick={() => setEditing(prev => ({...prev, photos: prev.photos.filter((_, j) => j !== i)}))} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-red-600 text-white flex items-center justify-center hover:bg-red-700" title="Удалить">
                        <X size={12} />
                      </button>
                      <div className="absolute bottom-0 inset-x-0 flex justify-between bg-black/55 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button type="button" onClick={() => movePhoto(i, i - 1)} disabled={i === 0} className="px-2 py-1 text-white disabled:opacity-30 hover:bg-black/30" title="Влево">‹</button>
                        <button type="button" onClick={() => movePhoto(i, i + 1)} disabled={i === (editing.photos || []).length - 1} className="px-2 py-1 text-white disabled:opacity-30 hover:bg-black/30" title="Вправо">›</button>
                      </div>
                    </div>
                  ))}
                  <label className="aspect-square border-2 border-dashed rounded flex flex-col items-center justify-center cursor-pointer hover:bg-[var(--color-bg-primary)] text-center" style={{ borderColor: "var(--color-border)" }}>
                    <ImageIcon size={20} className="text-[var(--color-text-secondary)] mb-1" />
                    <span className="text-[10px] text-[var(--color-text-secondary)] px-1">Выбрать<br />(можно несколько)</span>
                    <input type="file" accept="image/*" multiple onChange={uploadPhotos} className="hidden" data-testid="admin-car-photo-upload" />
                  </label>
                </div>
                <input className="field-input text-sm" placeholder="Или вставьте URL фото и нажмите Enter" onKeyDown={e => {
                  if (e.key === "Enter" && e.target.value) {
                    e.preventDefault();
                    setEditing(p => ({...p, photos: [...(p.photos || []), e.target.value]}));
                    e.target.value = "";
                  }
                }} />
              </div>
              <button onClick={save} className="btn-primary-cta w-full" data-testid="admin-save-car"><Save size={16} /> Сохранить</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ====== APPLICATIONS TAB ======
function ApplicationsTab() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/applications").then(r => setItems(r.data || []));
  useEffect(() => { load(); }, []);
  const remove = async (id) => { if (window.confirm("Удалить?")) { await api.delete(`/admin/applications/${id}`); load(); } };
  return (
    <div>
      <h2 className="font-headings font-bold text-xl mb-5">Заявки ({items.length})</h2>
      <div className="space-y-3">
        {items.map(a => (
          <div key={a.id} className="bg-white rounded-[var(--radius-lg)] p-5 border" style={{ borderColor: "var(--color-border)" }}>
            <div className="flex justify-between items-start gap-3">
              <div className="flex-1">
                <div className="font-headings font-bold">{a.name}</div>
                <div className="text-sm text-[var(--color-text-secondary)]">{a.phone} · {a.messenger}</div>
                {a.car_name && <div className="text-sm mt-1">Авто: <strong>{a.car_name}</strong></div>}
                {a.comment && <div className="text-sm mt-2 p-2 rounded bg-[var(--color-bg-primary)]">{a.comment}</div>}
                <div className="text-xs text-[var(--color-text-secondary)] mt-2">{new Date(a.created_at).toLocaleString("ru-RU")} · тип: {a.type}</div>
              </div>
              <button onClick={() => remove(a.id)} className="p-2 hover:bg-red-50 text-[var(--color-primary)] rounded"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="bg-white p-8 text-center text-[var(--color-text-secondary)] rounded-[var(--radius-lg)]">Заявок пока нет</div>}
      </div>
    </div>
  );
}

// ====== REVIEWS TAB ======
function ReviewsTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // null | {id?, author, text, car_name, rating, status, published_at}
  const load = () => api.get("/admin/reviews").then(r => setItems(r.data || []));
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => { await api.put(`/admin/reviews/${id}`, { status }); load(); };
  const remove = async (id) => { if (window.confirm("Удалить?")) { await api.delete(`/admin/reviews/${id}`); load(); } };

  const newReview = () => setEditing({
    author: "", text: "", car_name: "", rating: 5, status: "approved",
    published_at: new Date().toISOString().slice(0, 16), // YYYY-MM-DDTHH:mm для datetime-local
  });

  const startEdit = (r) => setEditing({
    id: r.id,
    author: r.author || "",
    text: r.text || "",
    car_name: r.car_name || "",
    rating: r.rating || 5,
    status: r.status || "approved",
    // ISO → формат для <input type="datetime-local">
    published_at: r.published_at ? new Date(r.published_at).toISOString().slice(0, 16) : new Date().toISOString().slice(0, 16),
  });

  const save = async () => {
    if (!editing.author.trim() || editing.text.trim().length < 5) {
      toast.error("Заполните имя и текст отзыва"); return;
    }
    const payload = {
      author: editing.author.trim(),
      text: editing.text.trim(),
      car_name: editing.car_name || "",
      rating: Number(editing.rating) || 5,
      status: editing.status,
      published_at: new Date(editing.published_at).toISOString(),
    };
    try {
      if (editing.id) await api.put(`/admin/reviews/${editing.id}`, payload);
      else await api.post("/admin/reviews", payload);
      toast.success("Отзыв сохранён");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Ошибка сохранения");
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-5">
        <h2 className="font-headings font-bold text-xl">Отзывы ({items.length})</h2>
        <button onClick={newReview} className="btn-primary-cta" data-testid="admin-review-new">Добавить отзыв</button>
      </div>

      {editing && (
        <div className="bg-white rounded-[var(--radius-lg)] p-5 border mb-5" style={{ borderColor: "var(--color-primary)" }} data-testid="admin-review-form">
          <h3 className="font-headings font-bold mb-4">{editing.id ? "Редактировать отзыв" : "Новый отзыв"}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input className="field-input" placeholder="Автор" value={editing.author} onChange={e => setEditing(p => ({...p, author: e.target.value}))} data-testid="admin-review-author" />
            <input className="field-input" placeholder="Купленный автомобиль" value={editing.car_name} onChange={e => setEditing(p => ({...p, car_name: e.target.value}))} />
            <select className="field-input" value={editing.rating} onChange={e => setEditing(p => ({...p, rating: e.target.value}))}>
              {[1,2,3,4,5].map(i => <option key={i} value={i}>{i} ⭐</option>)}
            </select>
            <select className="field-input" value={editing.status} onChange={e => setEditing(p => ({...p, status: e.target.value}))}>
              <option value="approved">Опубликован</option>
              <option value="pending">На модерации</option>
              <option value="rejected">Отклонён</option>
            </select>
            <div className="md:col-span-2">
              <label className="text-xs font-accent uppercase tracking-widest text-[var(--color-text-secondary)] mb-1 block">Дата публикации</label>
              <input type="datetime-local" className="field-input" value={editing.published_at} onChange={e => setEditing(p => ({...p, published_at: e.target.value}))} data-testid="admin-review-date" />
            </div>
            <textarea className="field-input md:col-span-2" rows={4} placeholder="Текст отзыва" value={editing.text} onChange={e => setEditing(p => ({...p, text: e.target.value}))} data-testid="admin-review-text" />
          </div>
          <div className="flex gap-3 mt-4">
            <button onClick={save} className="btn-primary-cta" data-testid="admin-review-save">Сохранить</button>
            <button onClick={() => setEditing(null)} className="px-5 py-3 rounded-[var(--radius-md)] border" style={{ borderColor: "var(--color-border)" }}>Отмена</button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {items.map(r => (
          <div key={r.id} className="bg-white rounded-[var(--radius-lg)] p-5 border" style={{ borderColor: "var(--color-border)" }}>
            <div className="flex justify-between items-start gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="font-headings font-bold">{r.author}</div>
                  <span className="text-xs px-2 py-0.5 rounded" style={{
                    background: r.status === "approved" ? "rgba(45,122,58,0.15)" : r.status === "rejected" ? "rgba(204,0,0,0.15)" : "rgba(230,126,34,0.15)",
                    color: r.status === "approved" ? "var(--color-success)" : r.status === "rejected" ? "var(--color-danger)" : "var(--color-warning)",
                  }}>
                    {r.status === "approved" ? "Опубликован" : r.status === "rejected" ? "Отклонён" : "На модерации"}
                  </span>
                  {r.published_at && (
                    <span className="text-xs text-[var(--color-text-secondary)]">
                      {new Date(r.published_at).toLocaleDateString("ru-RU", { day: "2-digit", month: "short", year: "numeric" })}
                    </span>
                  )}
                </div>
                <div className="text-sm text-[var(--color-text-secondary)] mt-1">
                  {r.phone && <span>{r.phone} · </span>}⭐ {r.rating}
                </div>
                <p className="mt-2 break-words">{r.text}</p>
                {r.car_name && <div className="text-xs text-[var(--color-text-secondary)] mt-1">{r.car_name}</div>}
              </div>
              <div className="flex flex-col gap-1 shrink-0">
                <button onClick={() => startEdit(r)} className="p-2 hover:bg-blue-50 text-[var(--color-secondary)] rounded" title="Редактировать"><Edit size={16} /></button>
                {r.status !== "approved" && <button onClick={() => setStatus(r.id, "approved")} className="p-2 hover:bg-green-50 text-[var(--color-success)] rounded" title="Одобрить"><Check size={16} /></button>}
                {r.status !== "rejected" && <button onClick={() => setStatus(r.id, "rejected")} className="p-2 hover:bg-orange-50 text-[var(--color-warning)] rounded" title="Отклонить"><EyeOff size={16} /></button>}
                <button onClick={() => remove(r.id)} className="p-2 hover:bg-red-50 text-[var(--color-primary)] rounded"><Trash2 size={16} /></button>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="bg-white p-8 text-center text-[var(--color-text-secondary)] rounded-[var(--radius-lg)]">Отзывов пока нет</div>}
      </div>
    </div>
  );
}

// ====== SERVICES TAB ======
function ServicesTab({ onChange }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/services").then(r => setItems(r.data || []));
  useEffect(() => { load(); }, []);
  const save = async () => {
    try {
      if (editing.id && items.find(i => i.id === editing.id)) await api.put(`/services/${editing.id}`, editing);
      else await api.post("/services", editing);
      toast.success("Сохранено"); setOpen(false); load(); onChange?.();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const remove = async (id) => { if (window.confirm("Удалить?")) { await api.delete(`/services/${id}`); load(); } };

  return (
    <div>
      <div className="flex justify-between mb-5">
        <h2 className="font-headings font-bold text-xl">Услуги ({items.length})</h2>
        <button onClick={() => { setEditing({ title: "", description: "", icon: "Wrench", sort_order: items.length }); setOpen(true); }} className="btn-primary-cta"><Plus size={16} /> Добавить</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.map(s => (
          <div key={s.id} className="bg-white rounded-[var(--radius-lg)] p-5 border" style={{ borderColor: "var(--color-border)" }}>
            <div className="flex justify-between items-start gap-3">
              <div className="flex-1">
                <div className="font-headings font-bold">{s.title}</div>
                <p className="text-sm text-[var(--color-text-secondary)] mt-1">{s.description}</p>
                <div className="text-xs text-[var(--color-text-secondary)] mt-2">Иконка: {s.icon}</div>
              </div>
              <div>
                <button onClick={() => { setEditing({...s}); setOpen(true); }} className="p-2 hover:bg-[var(--color-bg-primary)] rounded"><Edit size={16} /></button>
                <button onClick={() => remove(s.id)} className="p-2 hover:bg-red-50 text-[var(--color-primary)] rounded"><Trash2 size={16} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Услуга</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <input className="field-input" placeholder="Название" value={editing.title} onChange={e => setEditing(p => ({...p, title: e.target.value}))} />
              <textarea className="field-input" rows={3} placeholder="Описание" value={editing.description} onChange={e => setEditing(p => ({...p, description: e.target.value}))} />
              <input className="field-input" placeholder="Иконка (lucide-react имя, напр.: Search, Truck, Wrench)" value={editing.icon} onChange={e => setEditing(p => ({...p, icon: e.target.value}))} />
              <input className="field-input" type="number" placeholder="Порядок" value={editing.sort_order} onChange={e => setEditing(p => ({...p, sort_order: parseInt(e.target.value) || 0}))} />
              <button onClick={save} className="btn-primary-cta w-full"><Save size={16} /> Сохранить</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ====== SETTINGS TAB ======
function SettingsTab({ onChange }) {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/settings").then(r => setS(r.data)); }, []);
  if (!s) return <div className="skeleton h-96" />;

  const setField = (k, v) => setS(p => ({...p, [k]: v}));
  const setBg = (k, v) => setS(p => ({...p, bg_image_urls: {...p.bg_image_urls, [k]: v}}));
  const setDS = (k, v) => setS(p => ({...p, design_system: {...p.design_system, [k]: v}}));

  const save = async () => {
    try { await api.put("/settings", s); toast.success("Сохранено"); onChange?.(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const uploadBg = async (key, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try { const r = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); setBg(key, r.data.url); }
    catch (e) { toast.error("Ошибка загрузки"); }
  };

  const colorFields = [
    { key: "color_primary", label: "Основной (CTA)" },
    { key: "color_secondary", label: "Вторичный (шапка)" },
    { key: "color_bg_primary", label: "Светлый фон" },
    { key: "color_bg_dark", label: "Тёмный фон" },
    { key: "color_text_primary", label: "Основной текст" },
    { key: "color_text_secondary", label: "Вспомог. текст" },
    { key: "color_gold", label: "Золотой акцент" },
    { key: "color_success", label: "Успех (В наличии)" },
    { key: "color_warning", label: "Предупреждение" },
    { key: "color_danger", label: "Ошибка" },
  ];

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-[var(--radius-lg)] p-5 border" style={{ borderColor: "var(--color-border)" }}>
        <h3 className="font-headings font-bold text-lg mb-4">Основные данные</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input className="field-input" placeholder="Название сайта" value={s.site_title || ""} onChange={e => setField("site_title", e.target.value)} />
          <input className="field-input" placeholder="URL сайта (https://example.com)" value={s.site_url || ""} onChange={e => setField("site_url", e.target.value)} data-testid="settings-site-url" />
          <input className="field-input" placeholder="Telegram URL" value={s.telegram_url || ""} onChange={e => setField("telegram_url", e.target.value)} />
          <input className="field-input" placeholder="WhatsApp URL" value={s.whatsapp_url || ""} onChange={e => setField("whatsapp_url", e.target.value)} />
          <input className="field-input" placeholder="Telegram-канал" value={s.telegram_channel || ""} onChange={e => setField("telegram_channel", e.target.value)} />
          <input className="field-input" placeholder="Телефон" value={s.phone || ""} onChange={e => setField("phone", e.target.value)} />
          <input className="field-input" placeholder="Email" value={s.email || ""} onChange={e => setField("email", e.target.value)} />
          <input className="field-input" placeholder="Адрес" value={s.address || ""} onChange={e => setField("address", e.target.value)} />
          <input className="field-input" placeholder="Время работы" value={s.work_hours || ""} onChange={e => setField("work_hours", e.target.value)} />
        </div>
        <p className="text-xs text-[var(--color-text-secondary)] mt-3">💡 URL сайта обязателен для корректного sitemap.xml и Schema.org-разметки</p>
      </div>

      <TeamSection s={s} setS={setS} />

      <div className="bg-white rounded-[var(--radius-lg)] p-5 border" style={{ borderColor: "var(--color-border)" }}>
        <h3 className="font-headings font-bold text-lg mb-4">Цвета (Дизайн)</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {colorFields.map(f => (
            <div key={f.key} className="flex items-center gap-3 p-2 rounded border" style={{ borderColor: "var(--color-border)" }}>
              <input type="color" value={s.design_system?.[f.key] || "#000000"} onChange={e => setDS(f.key, e.target.value)} className="w-10 h-10 rounded cursor-pointer" />
              <div className="flex-1">
                <div className="text-xs text-[var(--color-text-secondary)] font-accent uppercase tracking-wider">{f.label}</div>
                <input type="text" value={s.design_system?.[f.key] || ""} onChange={e => setDS(f.key, e.target.value)} className="text-sm font-mono w-full bg-transparent" />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-[var(--radius-lg)] p-5 border" style={{ borderColor: "var(--color-border)" }}>
        <h3 className="font-headings font-bold text-lg mb-4">Фоновые изображения</h3>
        <div className="space-y-4">
          {Object.entries(BG_HINTS).map(([key, info]) => (
            <div key={key} className="flex flex-col md:flex-row gap-3">
              <div className="md:w-48 aspect-video rounded overflow-hidden border bg-[var(--color-bg-primary)] flex items-center justify-center" style={{ borderColor: "var(--color-border)" }}>
                {s.bg_image_urls?.[key] ? <img src={resolveImg(s.bg_image_urls[key])} alt="" className="w-full h-full object-cover" /> : <ImageIcon size={20} className="text-[var(--color-text-secondary)]" />}
              </div>
              <div className="flex-1">
                <div className="font-headings font-bold text-sm">{info.label}</div>
                <div className="text-xs text-[var(--color-text-secondary)] mb-2">{info.hint}</div>
                <div className="flex gap-2">
                  <input className="field-input flex-1 text-sm" placeholder="URL изображения" value={s.bg_image_urls?.[key] || ""} onChange={e => setBg(key, e.target.value)} />
                  <label className="btn-secondary-cta cursor-pointer text-xs px-3 py-2"><ImageIcon size={14} /> Загрузить
                    <input type="file" accept="image/*" className="hidden" onChange={e => e.target.files?.[0] && uploadBg(key, e.target.files[0])} />
                  </label>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <TwoFactorSection />
      <BackupsSection />

      <button onClick={save} className="btn-primary-cta" data-testid="admin-save-settings"><Save size={16} /> Сохранить настройки</button>
    </div>
  );
}

// ====== TEAM SECTION (managers within Settings) ======
function TeamSection({ s, setS }) {
  const team = s.contacts_info || [];
  const update = (i, field, val) => {
    const updated = [...team];
    updated[i] = { ...updated[i], [field]: val };
    setS(p => ({ ...p, contacts_info: updated }));
  };
  const add = () => setS(p => ({ ...p, contacts_info: [...team, { name: "", role: "", photo_url: "", telegram_url: "", whatsapp_url: "", is_active: true }] }));
  const remove = (i) => setS(p => ({ ...p, contacts_info: team.filter((_, j) => j !== i) }));
  const upload = async (i, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/admin/upload?watermark=false", fd, { headers: { "Content-Type": "multipart/form-data" } });
      update(i, "photo_url", r.data.url);
    } catch { toast.error("Ошибка загрузки"); }
  };

  return (
    <div className="bg-white rounded-[var(--radius-lg)] p-5 border" style={{ borderColor: "var(--color-border)" }} data-testid="team-section">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-headings font-bold text-lg flex items-center gap-2"><Users size={18} /> Команда / менеджеры ({team.length})</h3>
        <button onClick={add} className="btn-secondary-cta text-xs" data-testid="team-add"><Plus size={14} /> Добавить</button>
      </div>
      <div className="space-y-3">
        {team.map((m, i) => (
          <div key={i} className="grid grid-cols-1 md:grid-cols-[80px_1fr_auto] gap-3 p-3 rounded border" style={{ borderColor: "var(--color-border)" }}>
            <div className="relative">
              <div className="w-20 h-20 rounded-full overflow-hidden bg-[var(--color-bg-primary)] flex items-center justify-center border" style={{ borderColor: "var(--color-border)" }}>
                {m.photo_url ? <img src={resolveImg(m.photo_url)} alt="" className="w-full h-full object-cover" /> : <Users size={28} className="text-[var(--color-text-secondary)]" />}
              </div>
              <label className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-[var(--color-secondary)] flex items-center justify-center cursor-pointer">
                <ImageIcon size={12} className="text-white" />
                <input type="file" accept="image/*" className="hidden" onChange={e => e.target.files?.[0] && upload(i, e.target.files[0])} />
              </label>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <input className="field-input text-sm" placeholder="Имя" value={m.name} onChange={e => update(i, "name", e.target.value)} />
              <input className="field-input text-sm" placeholder="Должность" value={m.role} onChange={e => update(i, "role", e.target.value)} />
              <input className="field-input text-sm" placeholder="Telegram URL" value={m.telegram_url} onChange={e => update(i, "telegram_url", e.target.value)} />
              <input className="field-input text-sm" placeholder="WhatsApp URL" value={m.whatsapp_url} onChange={e => update(i, "whatsapp_url", e.target.value)} />
              <label className="flex items-center gap-2 text-sm md:col-span-2"><input type="checkbox" checked={m.is_active !== false} onChange={e => update(i, "is_active", e.target.checked)} /> Показывать на странице «Контакты»</label>
            </div>
            <button onClick={() => remove(i)} className="self-start p-2 hover:bg-red-50 text-[var(--color-primary)] rounded"><Trash2 size={16} /></button>
          </div>
        ))}
        {team.length === 0 && <p className="text-sm text-[var(--color-text-secondary)] text-center py-4">Менеджеров пока нет</p>}
      </div>
    </div>
  );
}

// ====== BACKUPS SECTION ======
function BackupsSection() {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/backups").then(r => setFiles(r.data.files || []));
  useEffect(() => { load(); }, []);
  const run = async () => {
    setBusy(true);
    try { const r = await api.post("/admin/backup"); setFiles(r.data.files || []); toast.success("Бэкап создан"); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-white rounded-[var(--radius-lg)] p-5 border" style={{ borderColor: "var(--color-border)" }} data-testid="backups-section">
      <div className="flex justify-between items-center mb-3">
        <h3 className="font-headings font-bold text-lg flex items-center gap-2"><Database size={18} /> Бэкапы базы данных</h3>
        <button onClick={run} disabled={busy} className="btn-primary-cta text-xs" data-testid="backup-run">
          {busy ? <Loader2 size={14} className="animate-spin" /> : "Создать сейчас"}
        </button>
      </div>
      <p className="text-sm text-[var(--color-text-secondary)] mb-3">Автоматически каждый день в 03:00 UTC. Хранятся последние 14 копий.</p>
      <div className="space-y-1 max-h-48 overflow-y-auto">
        {files.map(f => (
          <div key={f.name} className="flex items-center justify-between text-xs py-1.5 px-2 rounded hover:bg-[var(--color-bg-primary)]">
            <span className="font-mono">{f.name}</span>
            <span className="text-[var(--color-text-secondary)]">{(f.size / 1024).toFixed(1)} KB</span>
          </div>
        ))}
        {files.length === 0 && <p className="text-sm text-[var(--color-text-secondary)] text-center py-3">Бэкапов пока нет</p>}
      </div>
    </div>
  );
}

// ====== 2FA SECTION ======
function TwoFactorSection() {
  const [status, setStatus] = useState(null);
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = () => api.get("/auth/2fa/status").then(r => setStatus(r.data));
  useEffect(() => { refresh(); }, []);

  const start = async () => {
    setBusy(true);
    try { const r = await api.post("/auth/2fa/setup"); setSetup(r.data); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const verify = async () => {
    if (code.length !== 6) return toast.error("Введите 6 цифр");
    setBusy(true);
    try {
      await api.post("/auth/2fa/verify", { code });
      toast.success("2FA включена!");
      setSetup(null); setCode(""); refresh();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const disable = async () => {
    if (!window.confirm("Отключить 2FA?")) return;
    setBusy(true);
    try { await api.post("/auth/2fa/disable"); toast.success("2FA отключена"); refresh(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-[var(--radius-lg)] p-5 border" style={{ borderColor: "var(--color-border)" }} data-testid="2fa-section">
      <h3 className="font-headings font-bold text-lg mb-4 flex items-center gap-2">
        <Shield size={18} /> Двухфакторная аутентификация
        {status?.enabled && <span className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-700 font-accent uppercase tracking-wider">Включена</span>}
      </h3>

      {!status?.enabled && !setup && (
        <div>
          <p className="text-sm text-[var(--color-text-secondary)] mb-4">
            Защитите вход в админ-панель кодом из Google Authenticator (или другого TOTP-приложения).
          </p>
          <button onClick={start} disabled={busy} className="btn-primary-cta" data-testid="2fa-enable-btn">
            <Shield size={16} /> Включить 2FA
          </button>
        </div>
      )}

      {!status?.enabled && setup && (
        <div className="space-y-4">
          <p className="text-sm text-[var(--color-text-secondary)]">1. Отсканируйте QR-код в Google Authenticator</p>
          <div className="flex gap-4 items-start">
            <img src={setup.qr_code} alt="QR" className="w-40 h-40 border" style={{ borderColor: "var(--color-border)" }} />
            <div className="text-xs text-[var(--color-text-secondary)] flex-1 break-all">
              <div className="mb-1">Или введите ключ вручную:</div>
              <code className="bg-[var(--color-bg-primary)] px-2 py-1 rounded text-[10px]">{setup.secret}</code>
            </div>
          </div>
          <p className="text-sm text-[var(--color-text-secondary)]">2. Введите 6-значный код из приложения</p>
          <input className="field-input text-center text-2xl tracking-[0.4em] font-accent max-w-xs" placeholder="000000" maxLength={6} inputMode="numeric" value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ""))} data-testid="2fa-code-input" />
          <div className="flex gap-2">
            <button onClick={verify} disabled={busy || code.length !== 6} className="btn-primary-cta" data-testid="2fa-verify-btn">
              {busy ? <Loader2 size={16} className="animate-spin" /> : "Подтвердить"}
            </button>
            <button onClick={() => { setSetup(null); setCode(""); }} className="btn-secondary-cta">Отмена</button>
          </div>
        </div>
      )}

      {status?.enabled && (
        <div>
          <p className="text-sm text-[var(--color-text-secondary)] mb-4">
            2FA активна. При следующем входе потребуется код из вашего authenticator-приложения.
          </p>
          <button onClick={disable} disabled={busy} className="btn-secondary-cta" data-testid="2fa-disable-btn">
            <ShieldOff size={16} /> Отключить 2FA
          </button>
        </div>
      )}
    </div>
  );
}

// ====== CONTENT TAB ======
function ContentTab({ onChange }) {
  const [c, setC] = useState(null);
  useEffect(() => { api.get("/content").then(r => setC(r.data)); }, []);
  if (!c) return <div className="skeleton h-96" />;

  const setPath = (path, val) => {
    setC(prev => {
      const copy = JSON.parse(JSON.stringify(prev));
      const parts = path.split(".");
      let cur = copy;
      for (let i = 0; i < parts.length - 1; i++) {
        if (!cur[parts[i]]) cur[parts[i]] = {};
        cur = cur[parts[i]];
      }
      cur[parts[parts.length - 1]] = val;
      return copy;
    });
  };

  const save = async () => {
    try { await api.put("/content", c); toast.success("Сохранено"); onChange?.(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const Field = ({ label, path, multiline = false }) => {
    const parts = path.split(".");
    let v = c; for (const p of parts) v = v?.[p];
    return (
      <div>
        <label className="font-accent text-xs uppercase tracking-widest text-[var(--color-text-secondary)] mb-1 block">{label}</label>
        {multiline ? (
          <textarea className="field-input" rows={3} value={v || ""} onChange={e => setPath(path, e.target.value)} />
        ) : (
          <input className="field-input" value={v || ""} onChange={e => setPath(path, e.target.value)} />
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-[var(--radius-lg)] p-5 border" style={{ borderColor: "var(--color-border)" }}>
        <h3 className="font-headings font-bold text-lg mb-4">Hero (Главный экран)</h3>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Лейбл" path="hero.label" />
          <Field label="Подзаголовок" path="hero.subtitle" multiline />
          <Field label="Заголовок" path="hero.title" multiline />
          <Field label="Кнопка 1" path="hero.btn_primary" />
          <Field label="Кнопка 2" path="hero.btn_secondary" />
        </div>
      </div>

      <div className="bg-white rounded-[var(--radius-lg)] p-5 border" style={{ borderColor: "var(--color-border)" }}>
        <h3 className="font-headings font-bold text-lg mb-4">Статистика (3 числа)</h3>
        <div className="grid md:grid-cols-3 gap-3">
          <Field label="Число 1" path="stats.stat1_num" />
          <Field label="Подпись 1" path="stats.stat1_label" />
          <Field label="Число 2" path="stats.stat2_num" />
          <Field label="Подпись 2" path="stats.stat2_label" />
          <Field label="Число 3" path="stats.stat3_num" />
          <Field label="Подпись 3" path="stats.stat3_label" />
        </div>
      </div>

      <div className="bg-white rounded-[var(--radius-lg)] p-5 border" style={{ borderColor: "var(--color-border)" }}>
        <h3 className="font-headings font-bold text-lg mb-4">Каталог</h3>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Лейбл" path="catalog.label" />
          <Field label="Заголовок" path="catalog.title" />
          <Field label="Подзаголовок" path="catalog.subtitle" multiline />
        </div>
      </div>

      <div className="bg-white rounded-[var(--radius-lg)] p-5 border" style={{ borderColor: "var(--color-border)" }}>
        <h3 className="font-headings font-bold text-lg mb-4">Как мы работаем</h3>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Лейбл" path="how_we_work.label" />
          <Field label="Заголовок" path="how_we_work.title" />
          <Field label="Подзаголовок" path="how_we_work.subtitle" multiline />
        </div>
        <div className="mt-4 grid md:grid-cols-2 gap-3">
          {(c.how_we_work?.steps || []).map((step, i) => (
            <div key={i} className="border rounded p-3" style={{ borderColor: "var(--color-border)" }}>
              <div className="font-accent text-xs uppercase tracking-wider mb-2">Шаг {i + 1}</div>
              <input className="field-input mb-2" value={step.title} onChange={e => {
                const steps = [...c.how_we_work.steps];
                steps[i] = { ...steps[i], title: e.target.value };
                setPath("how_we_work.steps", steps);
              }} />
              <textarea className="field-input" rows={2} value={step.desc} onChange={e => {
                const steps = [...c.how_we_work.steps];
                steps[i] = { ...steps[i], desc: e.target.value };
                setPath("how_we_work.steps", steps);
              }} />
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-[var(--radius-lg)] p-5 border" style={{ borderColor: "var(--color-border)" }}>
        <h3 className="font-headings font-bold text-lg mb-4">CTA блок</h3>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Заголовок" path="cta_block.title" />
          <Field label="Кнопка" path="cta_block.btn_text" />
          <Field label="Подзаголовок" path="cta_block.subtitle" multiline />
        </div>
      </div>

      <div className="bg-white rounded-[var(--radius-lg)] p-5 border" style={{ borderColor: "var(--color-border)" }}>
        <h3 className="font-headings font-bold text-lg mb-4">О компании</h3>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Лейбл" path="about.label" />
          <Field label="Заголовок" path="about.title" />
          <Field label="Цитата" path="about.quote" multiline />
          <Field label="Текст" path="about.text" multiline />
        </div>
      </div>

      <div className="bg-white rounded-[var(--radius-lg)] p-5 border" style={{ borderColor: "var(--color-border)" }}>
        <h3 className="font-headings font-bold text-lg mb-4">Футер</h3>
        <div className="grid md:grid-cols-1 gap-3">
          <Field label="Описание" path="footer.description" multiline />
          <Field label="Копирайт" path="footer.copyright" />
        </div>
      </div>

      <button onClick={save} className="btn-primary-cta" data-testid="admin-save-content"><Save size={16} /> Сохранить контент</button>
    </div>
  );
}
